import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from '../services/supabase'
import { ArrowLeft } from 'lucide-react'
import PasswordStrengthBar, { isPasswordValid } from '../components/PasswordStrengthBar'

const errorMessages: Record<string, string> = {
  'New password should be different from the old password.': 'La nuova password deve essere diversa da quella precedente.',
}

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-sky-500'
const btnCls = 'w-full bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-medium rounded-lg py-2 text-sm transition-colors'

export default function ResetPassword() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [loading, setLoading] = useState(false)

  const confirmMismatch = confirm.length > 0 && confirm !== password

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isPasswordValid(password)) { setError('La password deve avere almeno 12 caratteri, una maiuscola e un numero'); return }
    if (password !== confirm) { setError('Le password non coincidono'); return }
    setError(''); setLoading(true)
    const { error } = await supabase.auth.updateUser({ password })
    setLoading(false)
    if (error) {
      // Capture unexpected auth errors (not user-facing validation like "same password")
      if (!errorMessages[error.message]) Sentry.captureException(error, { tags: { auth_action: 'reset_password' } })
      setError(errorMessages[error.message] ?? error.message)
    }
    else setDone(true)
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 w-full max-w-md">
          <h1 className="text-2xl font-bold mb-2">Password aggiornata</h1>
          <p className="text-gray-600 text-sm mb-6">Puoi ora accedere con la nuova password.</p>
          <a href="/" className="text-sky-600 text-sm hover:underline inline-flex items-center gap-1"><ArrowLeft size={14} /> Vai al login</a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold mb-6">Nuova password</h1>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Nuova password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Minimo 12 caratteri" required className={inputCls} />
            <PasswordStrengthBar password={password} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Conferma password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              placeholder="Ripeti la password" required className={inputCls} />
            {confirmMismatch && (
              <p className="text-red-500 text-xs mt-1">Le password non coincidono.</p>
            )}
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button type="submit" disabled={loading} className={btnCls}>
            {loading ? 'Salvataggio…' : 'Imposta nuova password'}
          </button>
        </form>
      </div>
    </div>
  )
}
